import { t } from 'i18next';
import { CEF_CODE } from 'pages/bank-account/constants';
import { defaultRegex } from 'utils/banks/banks';
import { removeNullFromObject } from 'utils/formatters';

export const ERROR_MESSAGES = {
  isRequired: t`required field`,
  isInvalid: t`invalid value for field`,
  isInvalidBank: t`choose a bank`,
};

export const getAccountToValidate = ({ account, accountType, digit, isCEFAccount }) => {
  if (isCEFAccount && !!account) {
    return `${accountType}${account}-${digit}`;
  }

  if (!account) {
    return null;
  }

  if (!digit) {
    return account;
  }

  return `${account}-${digit}`;
};

export const validateBankAccount = (value, bank) => {
  const defaultPattern = defaultRegex.account;
  const validationPattern = bank && bank.accountRegex ? bank.accountRegex : defaultPattern;
  const accountRegex = new RegExp(validationPattern);
  return accountRegex.test(value);
};

export const validateBankAgency = (value, bank) => {
  const defaultPattern = defaultRegex.agency;
  const validationPattern = bank && bank.agencyRegex ? bank.agencyRegex : defaultPattern;
  const agencyRegex = new RegExp(validationPattern);

  return agencyRegex.test(value);
};

export const getBankError = ({ accountError, agencyError, bankValue }) => {
  if ((!agencyError || !accountError) && !bankValue) {
    return [ERROR_MESSAGES.isInvalidBank];
  }

  return null;
};

export const getAccountTypeError = ({ accountTypeValue, bankValue }) => {
  if (bankValue && !accountTypeValue) {
    return [ERROR_MESSAGES.isRequired];
  }

  return null;
};

export const getBankAccountErrors = (value, bank) => {
  if (!value) {
    return [ERROR_MESSAGES.isRequired];
  }

  if (!validateBankAccount(value, bank)) {
    return [ERROR_MESSAGES.isInvalid];
  }

  return null;
};

export const getBankAgencyError = (value, bank) => {
  if (!value) {
    return [ERROR_MESSAGES.isRequired];
  }

  if (!validateBankAgency(value, bank)) {
    return [ERROR_MESSAGES.isInvalid];
  }

  return null;
};

const generateErrorsObject = ({ accountToValidate, accountTypeValue, fields, values }) => {
  const { ACCOUNT_TYPE, AGENCY, BANK, BANK_ACCOUNT, BANK_ACCOUNT_DIGIT } = fields;
  const errors = {};

  errors[BANK_ACCOUNT] = getBankAccountErrors(accountToValidate, values[BANK]);
  errors[BANK_ACCOUNT_DIGIT] = getBankAccountErrors(accountToValidate, values[BANK]);
  errors[ACCOUNT_TYPE] = getAccountTypeError({
    bankValue: values[BANK],
    accountTypeValue,
  });
  errors[AGENCY] = getBankAgencyError(values[AGENCY], values[BANK]);
  errors[BANK] = getBankError({
    accountError: errors[BANK_ACCOUNT],
    agencyError: errors[AGENCY],
    bankValue: values[BANK],
  });

  return removeNullFromObject(errors);
};

export const validateBankAccountForm = ({ fields, values }) => {
  const { ACCOUNT_TYPE, BANK, BANK_ACCOUNT, BANK_ACCOUNT_DIGIT } = fields;
  const accountTypeValue = values[ACCOUNT_TYPE]?.value;
  const accountToValidate = getAccountToValidate({
    account: values[BANK_ACCOUNT],
    accountType: accountTypeValue,
    digit: values[BANK_ACCOUNT_DIGIT],
    isCEFAccount: values[BANK]?.value === CEF_CODE,
  });

  return generateErrorsObject({
    accountTypeValue,
    accountToValidate,
    fields,
    values,
  });
};
